import { useEffect, useState } from "react";

const MAX_TIMEOUT_MS = 2147483647;

const getTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const getEventTimeStatus = (
  startDateTime,
  endDateTime,
  now = Date.now()
) => {
  const start = getTimestamp(startDateTime);
  const end = getTimestamp(endDateTime);

  if (!start || !end) {
    return {
      hasEventEnded: false,
      isOngoing: false,
      isUpcoming: false,
      canRate: false,
      nextTransitionAt: null,
    };
  }

  const hasEventEnded = now >= end;
  const isOngoing = now >= start && now < end;
  const isUpcoming = now < start;

  return {
    hasEventEnded,
    isOngoing,
    isUpcoming,
    canRate: hasEventEnded || isOngoing,
    nextTransitionAt: isUpcoming ? start : isOngoing ? end : null,
  };
};

const useEventTimeStatus = (startDateTime, endDateTime) => {
  const [status, setStatus] = useState(() =>
    getEventTimeStatus(startDateTime, endDateTime)
  );

  useEffect(() => {
    let timeoutId;
    let isActive = true;

    const scheduleNextUpdate = () => {
      const nextStatus = getEventTimeStatus(startDateTime, endDateTime);
      if (!isActive) return;

      setStatus(nextStatus);

      if (!nextStatus.nextTransitionAt) return;

      const delay = Math.max(0, nextStatus.nextTransitionAt - Date.now() + 250);
      timeoutId = window.setTimeout(
        scheduleNextUpdate,
        Math.min(delay, MAX_TIMEOUT_MS)
      );
    };

    scheduleNextUpdate();

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [startDateTime, endDateTime]);

  return status;
};

export default useEventTimeStatus;
